// app/(public)/consultation/page.tsx
"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api from "@/lib/axiosInstance";
import { ConsultFormData } from "@/lib/schemas/consultSchema";
import { ArrowLeft, ArrowRight, Sun, Shield, Clock, Users } from "lucide-react";

import ConsultStep2 from "@/components/onboarding/consultation/ConsultStep2";
import ConsultStep3 from "@/components/onboarding/consultation/ConsultStep3";
import ConsultStep4 from "@/components/onboarding/consultation/ConsultStep4";
import ConsultStep5 from "@/components/onboarding/consultation/ConsultStep5";
import ConsultStep6 from "@/components/onboarding/consultation/ConsultStep6";

const BENEFITS = [
  { icon: Sun, title: "Expert Solar Advice", desc: "Tailored recommendations from certified solar engineers." },
  { icon: Shield, title: "Verified Providers", desc: "Only work with our background-checked, verified installers." },
  { icon: Clock, title: "Fast Response", desc: "Hear back within 24 hours on any working day." },
  { icon: Users, title: "100% Free", desc: "No obligation. Our consultation service is completely free." },
];

const STEP_LABELS = ["Your Goal", "Energy Needs", "Location", "Contact Info"];

export default function ConsultationPage() {
  const router = useRouter();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<Partial<ConsultFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const goToNext = (data: any) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setStep((prev) => prev + 1);
  };

  const goToPrevious = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (data: ConsultFormData) => {
    setIsSubmitting(true);
    try {
      const finalData = { ...formData, ...data };
      await api.post("/consultations", finalData);
      toast.success("Thank you! Our team will contact you soon.");
      setFormData(finalData);
      setStep(5);
    } catch (error: any) {
      const msg =
        error.response?.data?.msg ||
        error.response?.data?.errors?.[0]?.message ||
        "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalSteps = 4;

  const progressBar = step < 5 ? (
    <div className="mt-4 mb-2">
      <div className="flex items-center gap-2 mb-1.5">
        {STEP_LABELS.map((label, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div className={`w-full h-1.5 rounded-full transition-all duration-300 ${i < step ? "bg-[#002301]" : "bg-gray-200"}`} />
            <span className={`text-[10px] hidden sm:block font-medium transition-colors ${i < step ? "text-[#002301]" : "text-gray-400"}`}>
              {label}
            </span>
          </div>
        ))}
      </div>
      <p className="text-center text-xs text-gray-400">Step {step} of {totalSteps}</p>
    </div>
  ) : null;

  const renderStep = () => {
    switch (step) {
      case 1:
        return <ConsultStep2 onBack={() => router.push("/")} onNext={goToNext} progressBar={progressBar} />;
      case 2:
        return <ConsultStep3 onBack={goToPrevious} onNext={goToNext} progressBar={progressBar} />;
      case 3:
        return <ConsultStep4 onBack={goToPrevious} onNext={goToNext} progressBar={progressBar} />;
      case 4:
        return (
          <ConsultStep5
            onBack={goToPrevious}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            defaultValues={formData as Partial<ConsultFormData>}
            progressBar={progressBar}
          />
        );
      case 5:
        return <ConsultStep6 onExplore={() => router.push("/marketplace")} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f6f6]">
      {/* Hero — matches financing page layout exactly */}
      <div className="bg-[#002301] pt-28 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <svg width="100%" height="100%">
            <defs><pattern id="con-dots" width="30" height="30" patternUnits="userSpaceOnUse"><circle cx="1.5" cy="1.5" r="1.5" fill="#17BC2E" /></pattern></defs>
            <rect width="100%" height="100%" fill="url(#con-dots)" />
          </svg>
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFB600]/8 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Link href="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <Sun className="w-3.5 h-3.5 text-[#FFB600]" />
              <span className="text-white/80 text-xs font-medium">Free Consultation</span>
            </span>
            <h1 className="text-white font-bold text-4xl sm:text-5xl leading-tight mb-4">
              Get Expert Solar<br />
              <span className="text-[#FFB600]">Advice. Free.</span>
            </h1>
            <p className="text-white/65 text-base leading-relaxed mb-8">
              Answer a few quick questions and our certified solar experts will create a personalised plan for your home or business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#consultation-form" className="inline-flex items-center justify-center gap-2 bg-[#FFB600] text-[#002301] px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-[#17BC2E] hover:text-white transition-all duration-300">
                Start Now <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>

          {/* Hero Image column */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="relative rounded-2xl overflow-hidden shadow-2xl hidden lg:block"
          >
            <img
              src="/assets/img/middle-grid-bg.jpg"
              alt="Solar consultation"
              className="w-full object-cover aspect-[4/3]"
            />
          </motion.div>
        </div>
      </div>

      {/* Form Section */}
      <div id="consultation-form" className="max-w-6xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

          {/* Form column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-6 sm:p-8">
                {renderStep()}
              </div>
            </div>
          </motion.div>

          {/* Benefits — visible on ALL screen sizes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="flex flex-col gap-4"
          >
            <div className="bg-[#002301] rounded-2xl p-6 text-white">
              <h3 className="font-bold text-base mb-1">Why consult with us?</h3>
              <p className="text-white/60 text-xs mb-5">Everything you need, at no cost.</p>
              <div className="space-y-4">
                {BENEFITS.map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#FFB600]/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon className="w-4 h-4 text-[#FFB600]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{title}</p>
                      <p className="text-xs text-white/60 mt-0.5 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
}
